import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import Helmet from 'react-helmet'
import {
  DefaultCloseIcon,
  modalStyles,
  Overlay,
  renderContent
} from './Modal.partials'
import { twMerge } from 'tailwind-merge'

export interface ModalProps {
  isOpen: boolean
  blockScroll?: boolean
  closeButton?: React.ReactElement
  closeOnOverlayClick?: boolean
  modalBody?: string | React.ReactElement
  modalFooter?: string | React.ReactElement
  modalHeader?: string | React.ReactElement
  onClose: () => void
  onCloseButtonClick?: () => void
  onOpen?: () => void
  overlay?: boolean
  style?: string
  isExtended?: boolean
  hideBodyOverflow?: boolean
  hideCloseButton?: boolean
  overlayClassName?: string
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  blockScroll = false,
  closeButton,
  closeOnOverlayClick = false,
  modalBody,
  modalFooter,
  modalHeader,
  onClose,
  onCloseButtonClick,
  onOpen = () => {},
  overlay = false,
  isExtended,
  style = '',
  hideBodyOverflow = true,
  hideCloseButton = false,
  overlayClassName
}) => {
  const handleClose = () => {
    onClose && onClose()
  }

  const handleEsc = (event: KeyboardEvent): void => {
    if (event.keyCode === 27) {
      handleClose()
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  useEffect(() => {
    if (hideBodyOverflow) {
      const originalOverflowStyle = window.getComputedStyle(
        document.body
      ).overflow

      if (onOpen && isOpen) {
        onOpen()
      }
      if (typeof document !== 'undefined') {
        const body = document.querySelector('body')
        if (blockScroll && isOpen) {
          body!.style.overflow = 'hidden'
        }

        return () => {
          body!.style.overflow = originalOverflowStyle
        }
      }
    }
    return () => {}
  }, [isOpen])

  return isOpen && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
        <>
          <Helmet>
            <meta
              name='viewport'
              content='width=device-width, initial-scale=1, maximum-scale=1'
            />
          </Helmet>
          {overlay && (
            <Overlay
              onClick={() => closeOnOverlayClick && handleClose()}
              className={overlayClassName}
            />
          )}
          <div
            className={twMerge(
              !isExtended
                ? modalStyles.container
                : modalStyles.extendedContainer,
              style
            )}
          >
            {!hideCloseButton && (
              <button
                className={modalStyles.button}
                onClick={
                  onCloseButtonClick
                    ? () => onCloseButtonClick()
                    : () => handleClose()
                }
              >
                {closeButton ? <>{closeButton}</> : <DefaultCloseIcon />}
              </button>
            )}
            {renderContent(modalHeader)}
            {renderContent(modalBody)}
            {renderContent(modalFooter)}
          </div>
        </>,
        document!.body
      )
    : null
}

export default Modal
